import { makeStyles } from 'tss-react/mui';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import ApiButton from 'web-library/stories/atom/Button/ApiButton';
import Button from 'web-library/stories/atom/Button/Button';
import Box from 'web-library/stories/atom/Box/Box';
import ErrorInfo from 'web-library/stories/molecule/ErrorInfo/ErrorInfo';
import Typography from 'web-library/stories/atom/Typography/Typography';
import { useResetPasswordForm } from '../logic/useResetPasswordForm';
import RouteKey from '../../../routes/key';
import { usePostLogout } from '../../../api-refactored/user/usePostLogout';
import { useGetResetLinkValidity } from '../../../api-refactored/user/useGetResetLinkValidity';

import {typographyTheme} from "web-library/theme/helper/tssHelper"
import {TextFieldForm} from "web-library";
const { error } = RouteKey.child;
const { invalidLink } = RouteKey.error.child;

const useStyles = makeStyles()((theme) =>
({
  formContainer: {
    padding: theme.spacing(2),
  },
  formLabel: {
     ...typographyTheme(theme, 'subtitle1'),
  },
  input: {
    marginBottom: theme.spacing(1),
    width: 'fit-content'
  },
  caption: {
    marginBottom: theme.spacing(1),
  },
  fieldError: {
    marginTop: theme.spacing(0),
    marginBottom: theme.spacing(1.5),
  },
  infoText: {
    color: theme.color.state.stateSuccess,
    marginTop: theme.spacing(3),
  },
}));

export interface PasswordFormProps {
  /** Indicate if it is set password page
   * */
  setPassword?: boolean;
}

const ResetPasswordForm = ({ setPassword }: PasswordFormProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const location = useLocation();
  const history = useHistory();
  const linkId = new URLSearchParams(location.search).get('id') ?? '';
  const [errMsg, setErrMsg] = useState();
  const [logoutButton, setLogoutButton] = useState(false);
  const { mutate: logout } = usePostLogout({
    refreshPage: true,
    redirectToHome: false,
  });

  if (!linkId) {
    history.replace(`/${error}/${invalidLink}`);
  }

  const {
    resetPasswordMutation,
    resetPassword,
    form,
    infoText,
  } = useResetPasswordForm();
  const { handleSubmit, formState: { errors }, control } = form;
  useGetResetLinkValidity({
    linkId,
    onSuccess: () => {
      setErrMsg(undefined);
      setLogoutButton(false);
    },
    onError: (err) => {
      switch (err.response?.status) {
        case 403:
          if (err.response?.data?.code === '1208') {
            setErrMsg(t('profile->>ResetPasswordForm.AuthenticatedSessionDetected'));
            setLogoutButton(true);
          } else if (err.response?.data?.code === '1209') {
            setErrMsg(t('profile->>ResetPasswordForm.LoggedInAtAnotherBrowser'));
            setLogoutButton(false);
          }
          break;
        default:
          history.replace(`/${error}/${invalidLink}`);
          break;
      }
    },
  });

  if (errMsg) {
    return (
      <Box>
        <Typography variant="subtitle1">{errMsg}</Typography>
        {logoutButton && (
          <Button onClick={() => logout()}>{t('profile->>ResetPasswordForm.Logout')}</Button>
        )}
      </Box>
    );
  }

  return (
    <Box className={classes.formContainer}>
      <form onSubmit={handleSubmit(resetPassword)}>
        <TextFieldForm
            form={form}
            error={!!errors.password}
            type="password"
            label={t('profile->>ResetPasswordForm.Password')}
            name="password"
            className={classes.input}
            autoComplete="new-password"
            id="new-password"
        />
        <ErrorInfo className={classes.fieldError} label={errors?.password?.message || ''} />

        <TextFieldForm
            form={form}
            error={!!errors.confirmPassword}
            type="password"
            label={t('profile->>ResetPasswordForm.ConfirmPassword')}
            name="confirmPassword"
            className={classes.input}
            autoComplete="new-password"
            id="confirm-password"
        />

        {errors?.confirmPassword ? (
          <ErrorInfo
            className={classes.fieldError}
            label={t('profile->>ResetPasswordForm.PasswordNotMatch')}
          />
        ) : null}

        <div className={classes.caption}>
          <Typography variant="caption">
            {t('profile->>ResetPasswordForm.PasswordRequirement')}
          </Typography>
        </div>
        <ApiButton status={resetPasswordMutation.status} type="submit">
          {setPassword
            ? t('profile->>ResetPasswordForm.SetPassword')
            : t('profile->>ResetPasswordForm.ResetPassword')}
        </ApiButton>

        {errors?.root?.backend?.message && <ErrorInfo label={errors.root.backend.message} />}
        {!!infoText.length && (
          <Typography variant="body2" className={classes.infoText}>
            {infoText}
          </Typography>
        )}
      </form>
    </Box>
  );
};
export default ResetPasswordForm;
